import { checkFeature } from '@bybeam/platform-lib/features/check';
import { FeatureName, PaymentMethod, PaymentStatus, ScheduleType } from '@bybeam/platform-types';
import useFulfillment from '@platform-ui-common/hooks/contexts/useFulfillment.js';
import useProgram from '@platform-ui-common/hooks/contexts/useProgram.js';
import { DateFormat, formatCurrency, formatDate } from '@platform-ui-common/utilities/format.js';
import {
  PaymentScheduleDisplay,
  findLatestPayment,
} from '@platform-ui-common/utilities/payment.js';
import useAuth from '@platform-ui/auth/hooks/useAuth/index.js';

export default function OutcomeMessage(): JSX.Element {
  const { user } = useAuth();
  const {
    payments: [payment],
    scheduleType,
  } = useFulfillment();
  const program = useProgram();
  const payeeIsCurrentUser = payment.payee.id === user?.core?.id;

  if (scheduleType === ScheduleType.Recurring) return <RecurringContent />;
  if (checkFeature(program?.features, FeatureName.PaymentsClaimFunds) && payeeIsCurrentUser)
    return <ClaimFundsContent />;
  if (payeeIsCurrentUser) return <PartnerIssuedApplicantContent />;
  return <OtherPartyOrVendorContent />;
}

function ClaimFundsContent(): JSX.Element {
  const {
    approvedAmount,
    payments: [payment],
  } = useFulfillment();
  const amount = formatCurrency(approvedAmount ?? 0, true);

  switch (payment.method) {
    case PaymentMethod.DirectDeposit:
      return (
        <>
          You requested to receive your {amount} grant through direct deposit. Your transfer has
          been initiated and your funds should be available in 1-3 business days.
        </>
      );
    case PaymentMethod.Check:
      return (
        <>
          You requested to receive your {amount} grant through paper check. Your transfer has been
          initiated and your funds should be available in 7-10 business days.
        </>
      );
    case PaymentMethod.PhysicalCard:
      return (
        <>
          You requested to receive your funds in the amount of {amount} via physical prepaid card.
          You should receive your card in the next 7 business days.
        </>
      );
    case PaymentMethod.VirtualCard:
      return (
        <>
          You requested to receive your funds in the amount of {amount} via a virtual prepaid card.
          You should have access to your funds immediately after creating an Akimbo Now prepaid card
          account.
        </>
      );
    case PaymentMethod.Zelle:
      return (
        <>
          You requested to receive your {amount} grant through zelle. Your transfer has been
          initiated and your funds should be available within minutes.
        </>
      );
    default:
      return <></>;
  }
}

function PartnerIssuedApplicantContent(): JSX.Element {
  const {
    payments: [payment],
  } = useFulfillment();

  switch (payment.method) {
    case PaymentMethod.DirectDeposit:
      return (
        <>
          The approved amount of {formatCurrency(payment.amount ?? 0, true)} has been sent through
          direct deposit. Please allow 1-3 business days for the direct deposit to be completed.
        </>
      );
    case PaymentMethod.Check:
      return (
        <>
          The approved amount of {formatCurrency(payment?.amount ?? 0, true)} has been sent to you
          as a paper check. You should receive your check in the mail in 7-10 business days.
        </>
      );
    case PaymentMethod.PhysicalCard:
      return (
        <>
          The approved amount of {formatCurrency(payment.amount ?? 0, true)} has been sent through a
          physical prepaid card. You should receive your card in the next 7 business days.
        </>
      );
    case PaymentMethod.VirtualCard:
      return (
        <>
          The approved amount of {formatCurrency(payment.amount ?? 0, true)} has been sent through a
          virtual prepaid card. You should have access to your funds immediately after creating an
          Akimbo Now prepaid card account.
        </>
      );
    default:
      return <></>;
  }
}

function OtherPartyOrVendorContent(): JSX.Element {
  const {
    payments: [payment],
  } = useFulfillment();

  switch (payment.method) {
    case PaymentMethod.DirectDeposit:
      return (
        <>
          The approved amount of {formatCurrency(payment.amount ?? 0, true)} has been sent to{' '}
          {payment.payee?.name ?? 'N/A'} via direct deposit. Payment should reach{' '}
          {payment.payee?.name ?? 'N/A'} in 1-3 business days.
        </>
      );
    case PaymentMethod.Check:
      return (
        <>
          The approved amount of {formatCurrency(payment.amount ?? 0, true)} has been sent. You can
          view payment details below.
        </>
      );
    default:
      return <></>;
  }
}

function RecurringContent(): JSX.Element {
  const { user } = useAuth();
  const {
    payments,
    payments: [firstPayment],
    paymentPattern,
  } = useFulfillment();
  const latestPayment = findLatestPayment(payments);
  const payment = latestPayment ?? firstPayment;
  const payeeIsCurrentUser = payment.payee.id === user?.core?.id;

  const props = {
    [PaymentMethod.DirectDeposit]: {
      method: 'direct deposit',
      delivery: 'Please allow 1-3 business days for the direct deposit to be completed.',
    },
    [PaymentMethod.Check]: {
      method: 'a paper check',
      delivery: 'The check should be received in the mail within 7-10 business days.',
    },
    [PaymentMethod.PhysicalCard]: { method: 'a physical prepaid card' },
    [PaymentMethod.VirtualCard]: { method: 'a virtual prepaid card' },
  };

  if (!latestPayment)
    return (
      <>
        {payeeIsCurrentUser ? 'Your' : 'The'} first recurring{' '}
        {PaymentScheduleDisplay[paymentPattern?.pattern] ?? 'N/A'} payment of{' '}
        {formatCurrency(paymentPattern?.amount ?? 0, true)} is scheduled to be sent through{' '}
        {props[payment.method].method} on {formatDate(payment.scheduledFor, DateFormat.ShortDate)}.
      </>
    );

  if (payment.status === PaymentStatus.Failed)
    return (
      <>
        {payeeIsCurrentUser ? 'Your' : 'The'} most recent{' '}
        {PaymentScheduleDisplay[paymentPattern?.pattern]} payment is processing.
      </>
    );

  return (
    <>
      {payeeIsCurrentUser ? 'Your' : 'The'} recurring{' '}
      {PaymentScheduleDisplay[paymentPattern?.pattern] ?? 'N/A'} payment of{' '}
      {formatCurrency(paymentPattern?.amount ?? 0, true)} has been sent through{' '}
      {props[payment.method].method}. {props[payment.method].delivery ?? ''}
    </>
  );
}
